import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { Participant, CriteriaGroup } from '@/types/evaluation.types';
import { ParticipantType } from '@/types/evaluation.types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Save, X } from 'lucide-react';

interface EditParticipantDialogProps {
  participant: Participant | null;
  existingParticipants: Participant[];
  criteriaGroups: CriteriaGroup[];
  isOpen: boolean;
  onClose: () => void;
  onSave: (participantId: string, updates: Partial<Participant>) => void;
}

export function EditParticipantDialog({
  participant,
  existingParticipants,
  criteriaGroups,
  isOpen,
  onClose,
  onSave,
}: EditParticipantDialogProps) {
  const { t } = useTranslation(['participants', 'common']);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [participantType, setParticipantType] = useState<ParticipantType>(ParticipantType.Evaluator);

  // Validation errors
  const [errors, setErrors] = useState<{
    name?: string;
    email?: string;
  }>({});

  // Initialize form when participant changes
  useEffect(() => {
    if (participant) {
      setName(participant.name);
      setEmail(participant.email);
      setPhone(participant.phone || '');
      setParticipantType(participant.participantType);
      setErrors({});
    }
  }, [participant]);

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Check for duplicate email (excluding current participant)
  const isDuplicateEmail = (email: string): boolean => {
    return existingParticipants.some(
      (p) => p.id !== participant?.id && p.email.toLowerCase() === email.toLowerCase()
    );
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    // Name validation
    if (!name.trim()) {
      newErrors.name = t('participants:addForm.validation.nameRequired');
    }

    // Email validation
    if (!email.trim()) {
      newErrors.email = t('participants:addForm.validation.emailRequired');
    } else if (!isValidEmail(email)) {
      newErrors.email = t('participants:addForm.validation.emailInvalid');
    } else if (isDuplicateEmail(email)) {
      newErrors.email = t('participants:addForm.validation.emailDuplicate');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSave = () => {
    if (!participant || !validateForm()) {
      return;
    }

    // Prepare updates
    const updates: Partial<Participant> = {
      name: name.trim(),
      email: email.trim().toLowerCase(),
      phone: phone.trim() || undefined,
      participantType,
    };

    onSave(participant.id, updates);
    onClose();
  };

  // Handle dialog close
  const handleClose = () => {
    setErrors({});
    onClose();
  };

  if (!participant) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('participants:editDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('participants:editDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Warning for changing participant type */}
          {participantType !== participant.participantType && (
            <Alert>
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                {t('participants:editDialog.typeChangeWarning')}
              </AlertDescription>
            </Alert>
          )}

          {/* Name field */}
          <div className="space-y-2">
            <Label htmlFor="edit-name">
              {t('participants:addForm.fields.name.label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-name"
              type="text"
              placeholder={t('participants:addForm.fields.name.placeholder')}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors({ ...errors, name: undefined });
                }
              }}
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'edit-name-error' : undefined}
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && (
              <p id="edit-name-error" className="text-sm text-destructive" role="alert">
                {errors.name}
              </p>
            )}
          </div>

          {/* Email field */}
          <div className="space-y-2">
            <Label htmlFor="edit-email">
              {t('participants:addForm.fields.email.label')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-email"
              type="email"
              placeholder={t('participants:addForm.fields.email.placeholder')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email) {
                  setErrors({ ...errors, email: undefined });
                }
              }}
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? 'edit-email-error' : undefined}
              className={errors.email ? 'border-destructive' : ''}
            />
            {errors.email && (
              <p id="edit-email-error" className="text-sm text-destructive" role="alert">
                {errors.email}
              </p>
            )}
          </div>

          {/* Phone field */}
          <div className="space-y-2">
            <Label htmlFor="edit-phone">
              {t('participants:addForm.fields.phone.label')}
            </Label>
            <Input
              id="edit-phone"
              type="tel"
              placeholder={t('participants:addForm.fields.phone.placeholder')}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          {/* Participant Type */}
          <div className="space-y-3">
            <Label>{t('participants:addForm.fields.type.label')} <span className="text-destructive">*</span></Label>
            <RadioGroup
              value={participantType}
              onValueChange={(value) => setParticipantType(value as ParticipantType)}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={ParticipantType.Host} id="edit-type-host" />
                <Label htmlFor="edit-type-host" className="font-normal cursor-pointer">
                  {t('participants:types.host')} - Full administrative access
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={ParticipantType.Evaluator} id="edit-type-evaluator" />
                <Label htmlFor="edit-type-evaluator" className="font-normal cursor-pointer">
                  {t('participants:types.evaluator')} - Can score assigned criteria groups
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value={ParticipantType.Viewer} id="edit-type-viewer" />
                <Label htmlFor="edit-type-viewer" className="font-normal cursor-pointer">
                  {t('participants:types.viewer')} - Read-only access
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Criteria group assignment info */}
          {participantType === ParticipantType.Evaluator && criteriaGroups.length > 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" aria-hidden="true" />
              <AlertDescription>
                {t('participants:editDialog.criteriaGroupInfo')}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            <X className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSave}>
            <Save className="mr-2 h-4 w-4" aria-hidden="true" />
            {t('common:actions.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
